import { useState, forwardRef, useImperativeHandle } from 'react';
import { IToolData } from 'types/products';
import { NudosButton, NudosTextArea, NudosUploadDownloadButton } from 'components/NudosComponents';
import { updateToolState } from 'services/toolsModule';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import BordRadioSelectDropdown, {
  IBordRadioSelectOption
} from 'components/NudosComponents/BordRadioSelectDropdown/BordRadioSelectDropdown';
import NudosConfirmActionModal from 'components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import './ProductEditState.scss';

const ProductEditState = forwardRef<{ hasUnsavedChanges: () => boolean }, IProductEditStateProps>(
  ({ productData, onClose, setRefreshProductData, refreshToolsListing }, ref) => {
  const { availableStatuses, status } = productData;
  const { id: statusId, comments } = status ?? {};
  const [selectedState, setSelectedState] = useState<string>(statusId?.toString() ?? '');
  const [reasonForModificationLocal, setReasonForModificationLocal] = useState<string>(comments ?? '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileUrl, setFileUrl] = useState<string | undefined>(productData?.status?.fileUrl ?? undefined);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const stateOptions: IBordRadioSelectOption[] =
    availableStatuses?.map(status => ({
      id: status?.id?.toString(),
      label: status?.name ?? '',
      value: status?.id?.toString(),
      description: status?.backdescription ?? ''
    })) ?? [];

  const hasUnsavedChanges = () => {
    const hasStateChanged = selectedState !== statusId?.toString();
    const hasCommentsChanged = reasonForModificationLocal !== (comments ?? '');
    const hasFileChanged = file !== undefined;
    return hasStateChanged || hasCommentsChanged || hasFileChanged;
  };

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges
  }));

  const handleCancel = () => {
    if(!hasUnsavedChanges()) return onClose(true);
    setShowConfirmationModal(true);
  };

  const handleExitWithoutSaving = () => {
    setSelectedState('available');
    setReasonForModificationLocal('');
    setShowConfirmationModal(false);
    onClose(true);
  };

  const handleContinueEditing = () => {
    setShowConfirmationModal(false);
  };

  const hasCommentsChanged = comments !== reasonForModificationLocal && reasonForModificationLocal?.length > 0;
  const disableButtonSave = isLoading || !selectedState || !reasonForModificationLocal || !hasCommentsChanged;
  const handleSave = async () => {
    setIsLoading(true);
    try {
      const data = {
        productId: productData.productId,
        toolStatusId: selectedState,
        comments: reasonForModificationLocal
      };
      const formData = new FormData();
      if (file) formData.append('file', file);
      formData.append('data', JSON.stringify(data));
      await updateToolState(formData);
      displaySuccessNotification({ customJSXMessage: <>El estado de la herramienta se ha modificado exitosamente</> });
      setRefreshProductData(true);
      refreshToolsListing?.();
      onClose(true);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (fileUploaded?: File) => {
    if (!fileUploaded) return;
    setFileUrl(fileUploaded?.name || '');
    setFile(fileUploaded);
  };

  const validatePDFCallback = (file?: File) => {
    if (!file) return false;
    const extension = file?.name.substring(file?.name.lastIndexOf('.')).toLowerCase();
    const isDocument = extension === '.pdf';
    return isDocument;
  };

  return (
    <div className="productEditState">
      <BordRadioSelectDropdown
        label="Estado"
        options={stateOptions}
        selectedValue={selectedState}
        onValueChange={setSelectedState}
        componentSize="fullWidth"
        placeholder="Selecciona una condición"
        customClassName="bordRadioSelectDropdownStateOptions"
        isOptionsContainerFullHeight
        customClassNameOptionsContainer="stateOptionsContainerCustom"
        customClassNameSelectOption="stateSelectOption"
      />
      <NudosTextArea
        label="Razón de la modificación"
        placeholder="Escribe aquí las razones..."
        currentValue={reasonForModificationLocal}
        setCurrentValue={setReasonForModificationLocal}
        customClassName="reasonForModificationTextArea"
        customTextAreaClassName="reasonForModificationTextAreaTextArea"
        customLabelClassName="reasonForModificationTextAreaLabel"
        maxLength={1000}
        noResize
      />
      <div className="uploadFileContainerState">
        <div className="uploadFileLabelState">Subir archivo PDF (Opcional)</div>
        <NudosUploadDownloadButton
          customClassName="uploadFileButtonState"
          uploadedFileHandler={handleFileUpload}
          downloadButtonText="Ver PDF subido"
          buttonEmptyText="Haz click aqui para subir archivo formato PDF"
          buttonFilledText="PDF o JPG"
          currentFileUrl={fileUrl}
          validateFileCallback={validatePDFCallback}
          customWidth="397px"
          customHeight="38px"
        />
      </div>

      <div className="containerButtons">
        <NudosButton
          buttonText="Cancelar modificación"
          componentSize="small"
          handleClick={handleCancel}
          colorPalette="secondary"
          isButtonLoading={isLoading}
          isButtonDisabled={isLoading}
        />
        <NudosButton
          buttonText="Modificar estado"
          handleClick={handleSave}
          isButtonLoading={isLoading}
          isButtonDisabled={disableButtonSave}
        />
      </div>

      {showConfirmationModal && (
        <NudosConfirmActionModal
          modalTitle="Tienes cambios sin guardar"
          modalText="Si sales, se perderá la información ingresada. ¿Seguro que quieres cancelar modificación del estado?"
          firstButtonText="Salir sin guardar"
          secondButtonText="Seguir editando"
          firstButtonAction={handleExitWithoutSaving}
          secondButtonAction={handleContinueEditing}
          customHeight="260px"
          customStyleModalTitle={
            { marginBottom: '5px' }
          }
        />
      )}
    </div>
  );
  }
);

export default ProductEditState;

interface IProductEditStateProps {
  productData: IToolData;
  onClose: (skipValidation?: boolean) => void;
  setRefreshProductData: React.Dispatch<React.SetStateAction<boolean>>;
  refreshToolsListing?: () => void;
}
